import type { PaymentMethodCode, TenantSettings } from '../api/types';

export const SUPPORTED_PAYMENT_METHODS: PaymentMethodCode[] = ['stripe', 'cash', 'check'];

/**
 * Payment methods the form can actually complete: enabled on the tenant, and — for card
 * payments — a Stripe publishable key must exist. A tenant with `stripe: true` but no key
 * (the sandbox until a Stripe test account is connected) can never finish a card setup,
 * so offering the card option would dead-end the checkout. The API enforces the same
 * rules server-side: `payment_method` is required, must be enabled on the account, and
 * `stripe` demands a real intent (verified against the sandbox 2026-09-01).
 */
export function usablePaymentMethods(
  settings: TenantSettings | null | undefined,
): PaymentMethodCode[] {
  const enabled = settings?.payment_methods ?? {};
  return SUPPORTED_PAYMENT_METHODS.filter(
    (m) => enabled[m] === true && (m !== 'stripe' || Boolean(settings?.stripe_public_key)),
  );
}

/**
 * Sandbox testing affordance: use a pasted `pk_test_…` key in place of the tenant's own, so the
 * card field renders and can be exercised on a sandbox tenant that exposes no key of its own.
 *
 * Ignored outside the sandbox — live checkouts always use the tenant's key, so a stray override
 * can never route real cards to another Stripe account. Note this only covers the BROWSER half:
 * the SetupIntent is still minted by the booking system's own Stripe account, so a tenant with no
 * Stripe connected answers 406 "account does not support Stripe" at submit (verified 2026-09-10).
 */
export function applyStripeKeyOverride<T extends TenantSettings>(
  settings: T,
  override: string | null | undefined,
  env: 'sandbox' | 'production',
): T {
  if (env !== 'sandbox' || !override) return settings;
  return { ...settings, stripe_public_key: override };
}
